import React from "react";
import ReactDOM from "react-dom";
import moment from "moment";
import { normalizeWebLanguage } from "../../webLanguages";
import "moment/locale/fr";
import "moment/locale/de";
import { getActivityType } from "./activityTypes";
import "./ActivityAlertsOverlay.css";

// 12% tint of the type color for the icon square
const tint = (hex) => `${hex}1f`;

/**
 * Stacked overlay of due-reminder cards (rendered in a portal on body).
 * Each card stays on screen until the user acts on it: complete, snooze
 * to tomorrow, or dismiss. Several due reminders stack vertically.
 *
 * Props: activities (array), t, lang, onComplete(a), onSnooze(a),
 * onDismiss(a), onDismissAll()
 */
const ActivityAlertsOverlay = ({
  activities,
  t,
  lang,
  onComplete,
  onSnooze,
  onDismiss,
  onDismissAll,
}) => {
  if (!activities || activities.length === 0) return null;

  const dueLabel = (activity) => {
    let m = null;
    if (activity.dueAt && activity.dueAt.toDate) m = moment(activity.dueAt.toDate());
    else if (activity.dueAtUnix) m = moment.unix(activity.dueAtUnix);
    if (!m) return "";
    m.locale(normalizeWebLanguage(lang));
    return m.calendar();
  };

  return ReactDOM.createPortal(
    <div className="aao-stack" role="region" aria-label={t("activities.webNotif.title")}>
      {activities.length > 1 && (
        <button type="button" className="aao-dismiss-all" onClick={onDismissAll}>
          <i className="fas fa-times mr-1"></i>
          {t("activities.alerts.dismissAll")}
        </button>
      )}
      {activities.map((activity) => {
        const id = activity.activityId || activity.id;
        const type = getActivityType(activity.activityType);
        return (
          <div key={id} className="aao-card" role="alert">
            <span
              className="aao-icon"
              style={{ backgroundColor: tint(type.color), color: type.color }}
            >
              <i className={`fas ${type.icon}`} aria-hidden="true"></i>
            </span>
            <div className="aao-body">
              <div className="aao-kicker">{t("activities.webNotif.title")}</div>
              <div className="aao-title">{activity.title}</div>
              {activity.note && <div className="aao-note">{activity.note}</div>}
              <div className="aao-due">
                <i className="far fa-clock mr-1" aria-hidden="true"></i>
                {dueLabel(activity)}
              </div>
              <div className="aao-actions">
                <button
                  type="button"
                  className="aao-action primary"
                  onClick={() => onComplete(activity)}
                >
                  <i className="fas fa-check mr-1"></i>
                  {t("activities.item.markDone")}
                </button>
                <button
                  type="button"
                  className="aao-action"
                  onClick={() => onSnooze(activity)}
                >
                  <i className="fas fa-arrow-right mr-1"></i>
                  {t("activities.date.tomorrow")}
                </button>
              </div>
            </div>
            <button
              type="button"
              className="aao-close"
              aria-label={t("common.close")}
              title={t("common.close")}
              onClick={() => onDismiss(activity)}
            >
              &times;
            </button>
          </div>
        );
      })}
    </div>,
    document.body
  );
};

export default ActivityAlertsOverlay;
